import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keyRegisterApi } from '../../api/keyRegisterApi';
import { todayStockholm } from '../../Helpers/leaseUtils';
import {
    KeyEvent,
    KeyEventInput,
    KeyFieldsInput,
    KeyHandOutInput,
    KeyType,
    KeyTypeAttachment,
    KeyTypeInput,
    RegisterKey,
} from '../../types/keyRegister';

export interface UseKeyRegisterReturn {
    /** Every type, archived included (sorted by name); filter on `archived` in the UI. */
    types: KeyType[];
    keys: RegisterKey[];
    /** Låsschema files, newest first. */
    lockSchedules: KeyTypeAttachment[];
    typesByKey: { [typeKey: string]: KeyType };
    keysByType: { [typeKey: string]: RegisterKey[] };
    /** YYYY-MM-DD in Stockholm, for overdue checks (`isOverdue`). */
    today: string;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;

    /** True while a mutation runs. A failed action rethrows its Swedish message for the caller to show. */
    submitting: boolean;
    createType: (input: KeyTypeInput) => Promise<KeyType>;
    updateType: (typeKey: string, input: KeyTypeInput) => Promise<void>;
    archiveType: (typeKey: string) => Promise<void>;
    unarchiveType: (typeKey: string) => Promise<void>;
    deleteType: (typeKey: string) => Promise<void>;
    /** Uploads, then soft-deletes, in one run (one reload). */
    saveTypeAttachments: (typeKey: string, filesToUpload: File[], attachmentKeysToRemove: string[]) => Promise<void>;
    getTypeAttachmentUrl: (storagePath: string) => Promise<string>;
    addLockSchedules: (files: File[]) => Promise<void>;
    removeLockSchedule: (fileKey: string) => Promise<void>;
    addKeys: (typeKey: string, count: number, startNumber?: number) => Promise<RegisterKey[]>;
    updateKeyFields: (keyKey: string, input: KeyFieldsInput) => Promise<void>;
    recordEvent: (keyKey: string, input: KeyEventInput) => Promise<KeyEvent>;
    /** "Lämna ut nycklar": several in-stock keys to one holder in one update. */
    handOutKeys: (keyKeys: string[], input: KeyHandOutInput) => Promise<KeyEvent[]>;
    getReceiptUrl: (storagePath: string) => Promise<string>;
}

/**
 * The whole nyckelregister (admin-only root), loaded once with `get()`;
 * every action reloads it after success. Spec docs/specs/key-register.md.
 * `enabled: false` skips the read (a page whose section the toggle hides).
 */
export const useKeyRegister = ({ enabled = true }: { enabled?: boolean } = {}): UseKeyRegisterReturn => {
    const [types, setTypes] = useState<KeyType[]>([]);
    const [keys, setKeys] = useState<RegisterKey[]>([]);
    const [lockSchedules, setLockSchedules] = useState<KeyTypeAttachment[]>([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const mounted = useRef(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await keyRegisterApi.getAll();
            if (!mounted.current) return;
            setTypes(data.types);
            setKeys(data.keys);
            setLockSchedules(data.lockSchedules);
        } catch (e) {
            console.error('Failed to load key register:', e);
            if (mounted.current) setError('Kunde inte hämta nyckelregistret');
        } finally {
            if (mounted.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        if (enabled) refresh();
        return () => {
            mounted.current = false;
        };
    }, [enabled, refresh]);

    const run = useCallback(async <T,>(fallback: string, action: () => Promise<T>): Promise<T> => {
        setSubmitting(true);
        try {
            const result = await action();
            await refresh();
            return result;
        } catch (e) {
            throw e instanceof Error && e.message ? e : new Error(fallback);
        } finally {
            if (mounted.current) setSubmitting(false);
        }
    }, [refresh]);

    const today = todayStockholm();
    const derived = useMemo(() => {
        const typesByKey: { [typeKey: string]: KeyType } = {};
        for (const type of types) typesByKey[type.key] = type;
        const keysByType: { [typeKey: string]: RegisterKey[] } = {};
        for (const key of keys) {
            if (!keysByType[key.typeKey]) keysByType[key.typeKey] = [];
            keysByType[key.typeKey].push(key);
        }
        return { typesByKey, keysByType };
    }, [types, keys]);

    return {
        types,
        keys,
        lockSchedules,
        ...derived,
        today,
        loading,
        error,
        refresh,
        submitting,
        createType: input => run('Kunde inte skapa nyckeltypen', () => keyRegisterApi.createType(input)),
        updateType: (typeKey, input) => run('Kunde inte spara nyckeltypen', () => keyRegisterApi.updateType(typeKey, input)),
        archiveType: typeKey => run('Kunde inte arkivera nyckeltypen', () => keyRegisterApi.archiveType(typeKey)),
        unarchiveType: typeKey => run('Kunde inte återställa nyckeltypen', () => keyRegisterApi.unarchiveType(typeKey)),
        deleteType: typeKey => run('Kunde inte ta bort nyckeltypen', () => keyRegisterApi.deleteType(typeKey)),
        saveTypeAttachments: (typeKey, filesToUpload, attachmentKeysToRemove) => run('Kunde inte spara filerna', async () => {
            if (filesToUpload.length > 0) await keyRegisterApi.addTypeAttachments(typeKey, filesToUpload);
            for (const attachmentKey of attachmentKeysToRemove) await keyRegisterApi.removeTypeAttachment(typeKey, attachmentKey);
        }),
        getTypeAttachmentUrl: storagePath => keyRegisterApi.getTypeAttachmentUrl(storagePath),
        addLockSchedules: files => run('Kunde inte ladda upp filerna', () => keyRegisterApi.addLockSchedules(files)),
        removeLockSchedule: fileKey => run('Kunde inte ta bort filen', () => keyRegisterApi.removeLockSchedule(fileKey)),
        addKeys: (typeKey, count, startNumber) => run('Kunde inte lägga till nycklar', () => keyRegisterApi.addKeys(typeKey, count, startNumber)),
        updateKeyFields: (keyKey, input) => run('Kunde inte spara nyckeln', () => keyRegisterApi.updateKeyFields(keyKey, input)),
        recordEvent: (keyKey, input) => run('Kunde inte spara händelsen', () => keyRegisterApi.recordEvent(keyKey, input)),
        handOutKeys: (keyKeys, input) => run('Kunde inte lämna ut nycklarna', () => keyRegisterApi.handOutKeys(keyKeys, input)),
        getReceiptUrl: storagePath => keyRegisterApi.getReceiptUrl(storagePath),
    };
};
